/* eslint-disable max-len */
import React, { HTMLProps, Ref, useRef, useState } from 'react';
import { classnames } from 'helpers/utils';

import { ChevronIcon } from 'assets/icons';
import { useClickOutside } from 'hooks/click-outside';
import { HighlightMatchedText } from 'common/highlight-matched-text';
import styles from './input-select.module.scss';

enum InputStyle {
  REGULAR = 'regular',
}

type InputSelectProps = {
  id: string;
  inputStyle?: InputStyle;
  disabled?: boolean;
  secondLabel?: string;
  errorMessage?: string;
  helperText?: string;
  className?: string;
  innerClassName?: string;
  onSubmit?: () => void;
  dataCy?: string;
  options: SelectType[];
  searchValue: string;
  optionSelected?: SelectType;
  onClickOption: (option: SelectType) => void;
  optionTextClass?: string;
  emptyMessage?: string;
  ref?: Ref<HTMLInputElement>;
};

const InputSelect: React.FC<InputSelectProps & HTMLProps<HTMLInputElement>> = ({
  id,
  label,
  disabled,
  secondLabel,
  errorMessage,
  className = '',
  innerClassName = '',
  onSubmit,
  dataCy,
  options,
  optionSelected,
  onClickOption,
  searchValue,
  emptyMessage = 'No results found',
  ref,
  ...props
}) => {
  const [show, setShow] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const onSelectOption = (option: SelectType) => {
    onClickOption(option);
    setShow(false);
    setEditMode(false);
  };

  useClickOutside(inputRef, () => {
    setShow(false);
    setEditMode(false);
  });

  const showOptions = () => (
    <div className={classnames(styles.optionsContent)}>
      {options.length === 0 && (
        <div
          className={classnames(
            'text__body__regular__medium__gray700',
            styles.item,
          )}
        >
          {emptyMessage}
        </div>
      )}
      {options.map((option) => (
        <button
          key={option.id}
          onClick={() => onSelectOption(option)}
          type="button"
          className={styles.item}
          data-cy={option.value}
        >
          <div
            className={classnames(
              'text__body__regular__medium__gray700',
              styles.optionTextClass,
              option.value === optionSelected?.value
                ? styles.optionSelected
                : '',
            )}
          >
            {optionSelected?.value === option.value ? (
              option.value
            ) : (
              <HighlightMatchedText
                text={option.value}
                query={searchValue}
                textStyleClass="text__body__regular__medium__gray700"
              />
            )}
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <div className={className} ref={inputRef}>
      {label && (
        <div className={classnames('mb-4')}>
          <label
            className={classnames(
              disabled
                ? 'text__body__semi__bold__medium__gray500'
                : 'text__body__semi__bold__medium__gray800',
            )}
            htmlFor={id}
          >
            <span>{label}</span>
            <span className="text__body__regular__medium__gray700">
              {secondLabel && secondLabel}
            </span>
          </label>
        </div>
      )}
      <div className={classnames(styles.inputInside)}>
        <input
          ref={ref}
          disabled={disabled}
          id={id}
          value={editMode ? searchValue : optionSelected?.value}
          className={classnames(
            styles.regular,
            innerClassName,
            'text__body__regular__small__gray700',
          )}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onSubmit) {
              onSubmit();
            }
          }}
          onFocus={() => {
            setShow(true);
            setEditMode(true);
          }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          data-cy={dataCy}
        />
        <ChevronIcon
          onClick={() => setShow(!show)}
          className={classnames(styles.chevron, {
            [styles.chevronRotate]: show,
            [styles.disableChevronColor]: !!disabled,
          })}
        />
        {show && showOptions()}
      </div>
    </div>
  );
};

export { InputStyle, InputSelect };
export type { InputSelectProps };
